import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import RevealWrapper from '@/components/base/RevealWrapper';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ContactFormBlock from '@/components/feature/ContactFormBlock';
import { CONTACT } from '@/mocks/contactInfo';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { localBusiness, website, service, faqPage, breadcrumb, article } from '@/lib/seo';

export default function ArticleScolopendre() {
  useDocumentHead({
    title: 'Morsure de scolopendre : symptômes, gestes, quand consulter',
    description: 'Mordu par une scolopendre en Guadeloupe ? Douleur, gonflement : les bons gestes immédiats, les signes qui imposent un médecin, et comment l\'éviter.',
    jsonLd: [article({ headline: 'Morsure de scolopendre : que faire', description: 'Douleur, gonflement : les bons gestes immédiats et quand consulter.', path: '/blogue/morsure-scolopendre-que-faire' }), breadcrumb([{ name: 'Accueil', path: '/' }, { name: 'Blogue', path: '/blogue' }, { name: 'Morsure de scolopendre', path: '/blogue/morsure-scolopendre-que-faire' }])],
  });

  return (
    <main>
      <Header />
      <Breadcrumb items={[{label:'Accueil',href:'/'},{label:'Blogue',href:'/blogue'},{label:'Morsure de scolopendre : que faire'}]} />

      {/* BANDEAU TITRE */}
      <section className="bg-primary-500 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Link
            to="/blogue"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-4 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            Retour au blogue
          </Link>
          <h1 className="font-heading font-black text-2xl md:text-4xl text-white leading-tight max-w-3xl">
            Morsure de scolopendre : que faire immédiatement (et quand consulter)
          </h1>
        </div>
      </section>

      {/* CONTENU */}
      <section className="bg-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <article className="max-w-2xl mx-auto">
            <RevealWrapper>
              <div className="prose-custom space-y-8">

                {/* INTRO */}
                <p className="text-foreground-700 text-base md:text-lg leading-relaxed">
                  La scolopendre — la bête à mille pattes de Guadeloupe — est l&rsquo;un des rares animaux de l&rsquo;île dont la morsure envoie parfois aux urgences. Douleur fulgurante, gonflement, inquiétude : voici exactement quoi faire, dans l&rsquo;ordre, et les signes qui doivent vous amener chez le médecin.
                </p>

                <p className="text-foreground-500 text-sm italic leading-relaxed">
                  Cet article est informatif et ne remplace pas un avis médical.
                </p>

                {/* IMAGE ILLUSTRATION */}
                <div className="w-full rounded-xl overflow-hidden">
                  <img
                    src="/images/blogue-morsure-scolopendre-que-faire-1.webp"
                    alt="Salle de bain créole en Guadeloupe où les scolopendres se cachent dans les zones humides et sombres"
                    className="w-full object-cover max-h-80 md:max-h-96"
                   loading="eager" fetchPriority="high" />
                </div>

                {/* H2 - RECONNAÎTRE */}
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 pt-4">
                  Reconnaître la morsure
                </h2>

                <p className="text-foreground-700 text-base leading-relaxed">
                  La scolopendre ne pique pas : elle mord avec deux crochets venimeux situés derrière la tête, en laissant souvent deux points rouges rapprochés. La douleur est immédiate, intense, souvent décrite comme une brûlure profonde — sans commune mesure avec une piqûre de guêpe. Suivent rougeur, gonflement, chaleur locale, parfois des ganglions sensibles. La douleur dure de quelques heures à 2-3 jours ; le gonflement peut persister davantage.
                </p>

                {/* H2 - LES BONS GESTES */}
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 pt-4">
                  Les bons gestes, dans l&rsquo;ordre
                </h2>

                <ol className="space-y-5 list-none pl-0">
                  <li className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-500 text-white font-heading font-bold text-sm shrink-0 mt-0.5">1</span>
                    <div>
                      <p className="text-foreground-700 text-sm leading-relaxed">Lavez la zone à l&rsquo;eau et au savon (comme toute plaie).</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-500 text-white font-heading font-bold text-sm shrink-0 mt-0.5">2</span>
                    <div>
                      <p className="text-foreground-700 text-sm leading-relaxed"><strong className="text-foreground-950">Chaleur d&rsquo;abord :</strong> le venin de scolopendre est thermolabile — de l&rsquo;eau chaude (chaude mais supportable, environ 45 °C, jamais brûlante) en immersion ou compresse pendant 20-30 minutes réduit nettement la douleur chez la plupart des gens.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-500 text-white font-heading font-bold text-sm shrink-0 mt-0.5">3</span>
                    <div>
                      <p className="text-foreground-700 text-sm leading-relaxed"><strong className="text-foreground-950">Froid ensuite</strong> contre le gonflement : glace enveloppée dans un linge, par périodes de 10-15 minutes.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-500 text-white font-heading font-bold text-sm shrink-0 mt-0.5">4</span>
                    <div>
                      <p className="text-foreground-700 text-sm leading-relaxed"><strong className="text-foreground-950">Antalgique si besoin</strong> (paracétamol en première intention), antihistaminique en cas de démangeaisons — demandez conseil à votre pharmacien.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-500 text-white font-heading font-bold text-sm shrink-0 mt-0.5">5</span>
                    <div>
                      <p className="text-foreground-700 text-sm leading-relaxed"><strong className="text-foreground-950">Surveillez la plaie</strong> les jours suivants : une morsure qui gonfle de plus en plus, chauffe ou suppure peut s&rsquo;infecter.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-500 text-white font-heading font-bold text-sm shrink-0 mt-0.5">6</span>
                    <div>
                      <p className="text-foreground-700 text-sm leading-relaxed"><strong className="text-foreground-950">Vérifiez votre vaccination tétanos</strong> — c&rsquo;est une plaie profonde.</p>
                    </div>
                  </li>
                </ol>

                {/* H2 - QUAND CONSULTER - ENCADRÉ */}
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 pt-4">
                  Quand consulter sans attendre
                </h2>

                <div className="bg-accent-100 border-l-4 border-accent-500 rounded-r-xl p-6 md:p-8">
                  <p className="text-foreground-950 font-semibold text-base mb-4">
                    Direction médecin ou urgences si :
                  </p>
                  <ul className="space-y-2.5 list-none pl-0">
                    <li className="flex items-start gap-3">
                      <i className="ri-alert-fill text-accent-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                      <span className="text-foreground-700 text-sm leading-relaxed">La personne mordue est un enfant en bas âge, une personne âgée ou immunodéprimée.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-alert-fill text-accent-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                      <span className="text-foreground-700 text-sm leading-relaxed">Des signes allergiques apparaissent (gonflement du visage ou de la gorge, difficulté à respirer, malaise, urticaire généralisée — appelez le 15).</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-alert-fill text-accent-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                      <span className="text-foreground-700 text-sm leading-relaxed">La douleur reste insupportable malgré les antalgiques.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-alert-fill text-accent-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                      <span className="text-foreground-700 text-sm leading-relaxed">Des signes d&rsquo;infection apparaissent après 24-48 h (fièvre, pus, traînée rouge).</span>
                    </li>
                  </ul>
                  <p className="text-foreground-600 text-sm leading-relaxed mt-4">
                    Chez l&rsquo;adulte en bonne santé, la morsure est très douloureuse mais rarement dangereuse.
                  </p>
                </div>

                {/* H2 - POURQUOI ELLE VOUS A MORDU */}
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 pt-4">
                  Pourquoi elle vous a mordu (et où elle se cachait)
                </h2>

                <p className="text-foreground-700 text-base leading-relaxed">
                  La scolopendre ne cherche pas l&rsquo;affrontement : elle mord quand on la surprend — en enfilant une chaussure, en soulevant une serviette au sol, en jardinant sans gants, en marchant pieds nus la nuit. Elle entre dans les maisons pour l&rsquo;humidité (salles de bain en carême) ou pour fuir la pluie (saison cyclonique), et se cache le jour dans les endroits sombres et frais : chaussures, linge au sol, dessous d&rsquo;évier, tas de bois contre la façade.
                </p>

                {/* H2 - ÉVITER LA PROCHAINE */}
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 pt-4">
                  Éviter la prochaine : les réflexes qui marchent
                </h2>

                <ul className="space-y-3 list-none pl-0">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base">Secouez chaussures et serviettes avant usage.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base">Ne laissez pas de linge au sol.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base">Gants pour jardiner et déplacer bois ou pierres.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base">Éloignez tas de bois, pierres et compost de la façade.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base">Bas de portes et grilles de soupiraux.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span className="text-foreground-700 text-base">Traquez l&rsquo;humidité intérieure.</span>
                  </li>
                </ul>

                <p className="text-foreground-700 text-base leading-relaxed">
                  Et si vous en croisez régulièrement chez vous, c&rsquo;est que votre terrain en héberge une population et que vos accès sont ouverts — là, une barrière périmétrique professionnelle autour de la maison règle le problème pour plusieurs mois.
                </p>
              </div>
            </RevealWrapper>
          </article>
        </div>
      </section>

      {/* BLOC NOTRE SOLUTION */}
      <section className="bg-background-100 py-12 md:py-16">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-2xl mx-auto">
              <div className="bg-secondary-100 border-l-4 border-secondary-500 rounded-r-xl p-6 md:p-8">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
                  Notre solution
                </h2>
                <p className="text-foreground-700 text-base leading-relaxed mb-6">
                  Nous traitons les scolopendres en Guadeloupe avec une barrière périmétrique qui neutralise les bêtes à mille pattes avant qu&rsquo;elles n&rsquo;entrent chez vous. Traitement des refuges, ceinture de protection autour de la maison, conseils de prévention durables. Protection de plusieurs mois.
                </p>
                <div className="flex flex-col sm:flex-row items-start gap-3 flex-wrap">
                  <Link
                    to="/scolopendres-guadeloupe"
                    className="inline-flex items-center gap-2 whitespace-nowrap px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-200 bg-secondary-500 text-white hover:bg-secondary-600 cursor-pointer"
                  >
                    Traitement scolopendres
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                  <Link
                    to="/tarifs"
                    className="inline-flex items-center gap-2 whitespace-nowrap px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-200 bg-white text-foreground-950 border border-background-300 hover:border-secondary-300 cursor-pointer"
                  >
                    Voir tous nos tarifs
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* CTA FINAL */}
      <section className="bg-primary-500 py-14 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-8">
            Vous en voyez souvent chez vous ?
          </h2>
          <p className="text-white/80 text-base leading-relaxed max-w-xl mx-auto mb-8">
            Dites-le-nous sur WhatsApp : conseils gratuits et devis en 2 h.
          </p>
          <a
            href={CONTACT.whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap px-8 py-3.5 rounded-lg font-semibold text-base transition-all duration-200 bg-whatsapp-500 text-white hover:bg-whatsapp-600 cursor-pointer"
          >
            <i className="ri-whatsapp-line text-xl w-5 h-5 flex items-center justify-center"></i>
            WhatsApp direct
          </a>
        </div>
      </section>

      {/* MAILLAGE */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto space-y-3">
            <p className="text-center text-foreground-500 text-sm uppercase tracking-wider mb-4">À lire aussi</p>
            <Link
              to="/scolopendres-guadeloupe"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Notre service anti-scolopendres
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Barrière périmétrique, traitement des refuges, protection plusieurs mois
              </span>
            </Link>
            <Link
              to="/traitement-ravets-cafards-guadeloupe"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Traitement ravets + scolopendres
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Tarif groupé : les deux partagent les mêmes points d&rsquo;entrée
              </span>
            </Link>
            <Link
              to="/desinsectisation-guadeloupe"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Tous nos traitements insectes
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Désinsectisation complète partout en Guadeloupe
              </span>
            </Link>
            <Link
              to="/tarifs"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Consultez nos tarifs
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Prix transparents, devis gratuit en 2 h
              </span>
            </Link>
          </div>
        </div>
      </section>

      <ContactFormBlock source="blogue-scolopendre" />

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}